import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  draft: { label: 'مسودة', cls: 'bg-gray-200 text-gray-800' },
  under_review: { label: 'قيد المراجعة', cls: 'bg-yellow-200 text-yellow-800' },
  approved: { label: 'معتمد', cls: 'bg-blue-200 text-blue-800' },
  partially_paid: { label: 'مدفوع جزئياً', cls: 'bg-orange-200 text-orange-800' },
  paid: { label: 'مدفوع', cls: 'bg-green-200 text-green-800' },
  cancelled: { label: 'ملغى', cls: 'bg-red-200 text-red-800' },
};

const fmt = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const PayrollManagement = ({ user }) => {
  const isAdmin = user?.role === 'super_admin' || user?.role === 'general_accountant';
  const [tab, setTab] = useState('dashboard');
  const [agencies, setAgencies] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/agencies`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setAgencies(r.data || []);
      } catch (e) { /* silent */ }
    })();
  }, []);

  if (!isAdmin) {
    return (
      <div className="p-8 text-center bg-red-50 border border-red-300 rounded-lg" data-testid="payroll-denied">
        <div className="text-4xl mb-2">🔒</div>
        <h3 className="text-lg font-bold text-red-800">وحدة الرواتب سرية</h3>
        <p className="text-sm text-red-700 mt-1">
          مصرّح للمدير العام والمحاسب العام فقط بالوصول إلى هذه الوحدة.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" dir="rtl" data-testid="payroll-page">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">💰 الرواتب والأجور</h2>
        <Badge className="bg-purple-100 text-purple-800 border border-purple-300">
          🔐 وصول مقيّد — المدير/المحاسب فقط
        </Badge>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-6 w-full">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">لوحة الرواتب</TabsTrigger>
          <TabsTrigger value="employees" data-testid="tab-employees">الموظفون</TabsTrigger>
          <TabsTrigger value="periods" data-testid="tab-periods">الرواتب الشهرية</TabsTrigger>
          <TabsTrigger value="advances" data-testid="tab-advances">السلف</TabsTrigger>
          <TabsTrigger value="deductions" data-testid="tab-deductions">الغيابات/الخصومات</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">الإعدادات</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard"><DashboardTab agencies={agencies} /></TabsContent>
        <TabsContent value="employees"><EmployeesTab agencies={agencies} /></TabsContent>
        <TabsContent value="periods"><PeriodsTab agencies={agencies} /></TabsContent>
        <TabsContent value="advances"><AdvancesTab /></TabsContent>
        <TabsContent value="deductions"><DeductionsTab /></TabsContent>
        <TabsContent value="settings"><SettingsTab /></TabsContent>
      </Tabs>
    </div>
  );
};

// ─────────────────────────── Dashboard ───────────────────────────
const DashboardTab = ({ agencies }) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [agencyId, setAgencyId] = useState('all');
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const q = new URLSearchParams({ year, month });
      if (agencyId && agencyId !== 'all') q.set('agency_id', agencyId);
      const r = await axios.get(`${API}/payroll/dashboard?${q}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setData(r.data);
    } catch (e) { alert('❌ ' + (e.response?.data?.detail || e.message)); }
  }, [year, month, agencyId]);

  useEffect(() => { load(); }, [load]);

  const kpi = data?.kpi || {};
  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap items-end bg-white p-3 rounded border">
        <div><Label>السنة</Label><Input type="number" value={year} onChange={e=>setYear(+e.target.value)} className="w-24" /></div>
        <div><Label>الشهر</Label><Input type="number" min="1" max="12" value={month} onChange={e=>setMonth(+e.target.value)} className="w-20" /></div>
        <div className="flex-1 min-w-[200px]">
          <Label>الوكالة</Label>
          <Select value={agencyId} onValueChange={setAgencyId}>
            <SelectTrigger data-testid="dashboard-agency-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">كل الوكالات</SelectItem>
              {agencies.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={load}>🔄 تحديث</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ['👥 عدد الموظفين', kpi.employee_count],
          ['💰 إجمالي الأجر (Gross)', fmt(kpi.gross)],
          ['💵 صافي الأجور (Net)', fmt(kpi.net)],
          ['🏛️ CNAS موظف', fmt(kpi.cnas_employee)],
          ['🏛️ CNAS رب العمل', fmt(kpi.cnas_employer)],
          ['🧾 IRG', fmt(kpi.irg)],
          ['💳 السلف المستقطعة', fmt(kpi.advances)],
          ['🚫 الغيابات', fmt(kpi.absences)],
          ['💼 تكلفة إجمالية للموظفين', fmt(kpi.employer_cost)],
          ['✅ عناصر مدفوعة', kpi.paid_count || 0],
          ['⏳ عناصر غير مدفوعة', kpi.unpaid_count || 0],
        ].map(([label, val]) => (
          <Card key={label}>
            <CardContent className="p-3">
              <div className="text-xs text-gray-600">{label}</div>
              <div className="text-lg font-bold" data-testid={`kpi-${label}`}>{val ?? 0}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {data?.periods?.length > 0 && (
        <Card>
          <CardHeader><CardTitle>فترات الشهر</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow>
                <TableHead>الرقم</TableHead><TableHead>الوكالة</TableHead>
                <TableHead>عدد الموظفين</TableHead><TableHead>Net</TableHead>
                <TableHead>الحالة</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.periods.map(p => {
                  const st = STATUS_BADGE[p.status] || STATUS_BADGE.draft;
                  const ag = agencies.find(a => a.id === p.agency_id);
                  return (
                    <TableRow key={p.id}>
                      <TableCell className="font-mono">{p.period_no}</TableCell>
                      <TableCell>{ag?.name || (p.agency_id ? '—' : 'كل الوكالات')}</TableCell>
                      <TableCell>{p.employee_count}</TableCell>
                      <TableCell className="font-mono">{fmt(p.totals?.net)}</TableCell>
                      <TableCell><span className={`text-xs px-2 py-1 rounded ${st.cls}`}>{st.label}</span></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ─────────────────────────── Employees ───────────────────────────
const EmployeesTab = ({ agencies }) => {
  const [list, setList] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/payroll/employees`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setList(r.data || []);
    } catch (e) { alert('❌ ' + (e.response?.data?.detail || e.message)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = (emp) => {
    setEditing(emp);
    setForm(emp ? { ...emp } : {
      full_name: '', agency_id: agencies[0]?.id || '', job_title: '',
      hire_date: new Date().toISOString().split('T')[0], contract_type: 'cdi',
      status: 'active', payment_method: 'bank', basic_salary: 0,
    });
    setShowDialog(true);
  };

  const save = async () => {
    try {
      const token = localStorage.getItem('token');
      if (editing) {
        await axios.put(`${API}/payroll/employees/${editing.id}`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${API}/payroll/employees`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setShowDialog(false);
      load();
    } catch (e) { alert('❌ ' + (e.response?.data?.detail || e.message)); }
  };

  const terminate = async (id) => {
    if (!window.confirm('تعطيل الموظف؟ (لا يحذف، فقط status=terminated)')) return;
    try {
      await axios.delete(`${API}/payroll/employees/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      load();
    } catch (e) { alert('❌ ' + (e.response?.data?.detail || e.message)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between">
        <h3 className="text-lg font-bold">قائمة الموظفين ({list.length})</h3>
        <Button onClick={() => open(null)} data-testid="add-employee-btn">➕ إضافة موظف</Button>
      </div>
      <Table>
        <TableHeader><TableRow>
          <TableHead>الرقم</TableHead><TableHead>الاسم</TableHead>
          <TableHead>الوكالة</TableHead><TableHead>الوظيفة</TableHead>
          <TableHead>الأساسي</TableHead><TableHead>الحالة</TableHead>
          <TableHead>إجراءات</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {list.map(e => (
            <TableRow key={e.id}>
              <TableCell className="font-mono">{e.employee_no}</TableCell>
              <TableCell className="font-semibold">{e.full_name}</TableCell>
              <TableCell>{e.agency_name || '—'}</TableCell>
              <TableCell>{e.job_title}</TableCell>
              <TableCell className="font-mono">{fmt(e.basic_salary)}</TableCell>
              <TableCell><Badge className={e.status === 'active' ? 'bg-green-100' : 'bg-gray-100'}>{e.status}</Badge></TableCell>
              <TableCell>
                <Button variant="outline" size="sm" onClick={() => open(e)} data-testid={`edit-emp-${e.id}`}>✏️</Button>
                {e.status === 'active' && (
                  <Button variant="outline" size="sm" className="mr-1 text-red-600"
                          onClick={() => terminate(e.id)}>🚫</Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-xl" data-testid="employee-dialog">
          <DialogHeader><DialogTitle>{editing ? 'تعديل موظف' : 'موظف جديد'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>الاسم الكامل *</Label>
              <Input value={form.full_name||''} onChange={e=>setForm({...form,full_name:e.target.value})} data-testid="emp-name" /></div>
            <div><Label>الوظيفة *</Label>
              <Input value={form.job_title||''} onChange={e=>setForm({...form,job_title:e.target.value})} /></div>
            <div><Label>الوكالة *</Label>
              <Select value={form.agency_id} onValueChange={v=>setForm({...form,agency_id:v})}>
                <SelectTrigger data-testid="emp-agency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {agencies.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label>الراتب الأساسي (د.ج) *</Label>
              <Input type="number" value={form.basic_salary||0} onChange={e=>setForm({...form,basic_salary:+e.target.value})} data-testid="emp-salary" /></div>
            <div><Label>تاريخ التوظيف</Label>
              <Input type="date" value={form.hire_date||''} onChange={e=>setForm({...form,hire_date:e.target.value})} /></div>
            <div><Label>نوع العقد</Label>
              <Select value={form.contract_type||'cdi'} onValueChange={v=>setForm({...form,contract_type:v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cdi">CDI</SelectItem>
                  <SelectItem value="cdd">CDD</SelectItem>
                  <SelectItem value="other">آخر</SelectItem>
                </SelectContent>
              </Select></div>
            <div><Label>الهاتف</Label>
              <Input value={form.phone||''} onChange={e=>setForm({...form,phone:e.target.value})} /></div>
            <div><Label>CNAS</Label>
              <Input value={form.cnas_number||''} onChange={e=>setForm({...form,cnas_number:e.target.value})} /></div>
            <div><Label>RIB (البنك)</Label>
              <Input value={form.rib||''} onChange={e=>setForm({...form,rib:e.target.value})} /></div>
            <div><Label>طريقة الدفع</Label>
              <Select value={form.payment_method||'bank'} onValueChange={v=>setForm({...form,payment_method:v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank">تحويل بنكي</SelectItem>
                  <SelectItem value="cash">نقداً</SelectItem>
                </SelectContent>
              </Select></div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={()=>setShowDialog(false)}>إلغاء</Button>
            <Button onClick={save} data-testid="emp-save">💾 حفظ</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─────────────────────────── Periods ───────────────────────────
const PeriodsTab = ({ agencies }) => {
  const [periods, setPeriods] = useState([]);
  const [selected, setSelected] = useState(null);
  const now = new Date();
  const [newY, setNewY] = useState(now.getFullYear());
  const [newM, setNewM] = useState(now.getMonth() + 1);
  const [newAg, setNewAg] = useState('all');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/payroll/periods`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setPeriods(r.data || []);
    } catch (e) { alert('❌ ' + (e.response?.data?.detail || e.message)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      const body = { year: newY, month: newM };
      if (newAg !== 'all') body.agency_id = newAg;
      const r = await axios.post(`${API}/payroll/periods`, body, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      alert(`✅ تم إنشاء ${r.data.period_no} (${r.data.employee_count} موظف)`);
      load();
    } catch (e) { alert('❌ ' + (e.response?.data?.detail || e.message)); }
  };

  const openDetails = async (p) => {
    try {
      const r = await axios.get(`${API}/payroll/periods/${p.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setSelected(r.data);
    } catch (e) { alert('❌ ' + (e.response?.data?.detail || e.message)); }
  };

  const doAction = async (path, msg) => {
    try {
      const r = await axios.post(`${API}/payroll/periods/${selected.period.id}/${path}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      alert(`✅ ${msg}${r.data.accounting_entry_no ? '\nقيد محاسبي: ' + r.data.accounting_entry_no : ''}`);
      await openDetails(selected.period);
      load();
    } catch (e) { alert('❌ ' + (e.response?.data?.detail || e.message)); }
  };

  const payItem = async (item) => {
    if (!window.confirm(`دفع ${fmt(item.net_salary)} د.ج للموظف ${item.employee_name}؟`)) return;
    try {
      await axios.post(`${API}/payroll/items/${item.id}/pay`, { method: item.payment_method || 'bank' }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      openDetails(selected.period);
      load();
    } catch (e) { alert('❌ ' + (e.response?.data?.detail || e.message)); }
  };

  const openPayslip = (item) => {
    const token = localStorage.getItem('token');
    // Fetch via authorized request then open the resulting blob.
    axios.get(`${API}/payroll/items/${item.id}/payslip.pdf`, {
      headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
    }).then(r => {
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    }).catch(e => alert('❌ ' + e.message));
  };

  if (selected) {
    const st = STATUS_BADGE[selected.period.status] || STATUS_BADGE.draft;
    const canEdit = selected.period.status === 'draft';
    const canApprove = ['draft','under_review'].includes(selected.period.status);
    const canPay = ['approved','partially_paid'].includes(selected.period.status);
    return (
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <div>
            <Button variant="outline" onClick={()=>setSelected(null)}>← رجوع</Button>
            <span className="font-mono font-bold mr-3">{selected.period.period_no}</span>
            <Badge className={st.cls}>{st.label}</Badge>
          </div>
          <div className="flex gap-2">
            {canEdit && <Button variant="outline" onClick={()=>doAction('recalculate','أُعيد الحساب')}>🧮 إعادة حساب</Button>}
            {canApprove && <Button onClick={()=>doAction('approve','تم الاعتماد')} data-testid="approve-btn">✅ اعتماد</Button>}
            {['draft','under_review','approved'].includes(selected.period.status) &&
              <Button variant="outline" className="text-red-700" onClick={()=>doAction('cancel','تم الإلغاء')}>❌ إلغاء</Button>}
          </div>
        </div>
        {selected.period.accounting_entry_no && (
          <div className="text-xs bg-blue-50 border border-blue-200 rounded p-2">
            📒 القيد المحاسبي: <b>{selected.period.accounting_entry_no}</b>
          </div>
        )}
        <Table>
          <TableHeader><TableRow>
            <TableHead>الموظف</TableHead><TableHead>الأساسي</TableHead>
            <TableHead>Gross</TableHead><TableHead>CNAS</TableHead>
            <TableHead>IRG</TableHead><TableHead>سلف</TableHead>
            <TableHead>Net</TableHead><TableHead>الحالة</TableHead>
            <TableHead>إجراءات</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {selected.items.map(it => (
              <TableRow key={it.id}>
                <TableCell>{it.employee_name}<div className="text-xs text-gray-500">{it.employee_no}</div></TableCell>
                <TableCell className="font-mono">{fmt(it.basic_salary)}</TableCell>
                <TableCell className="font-mono">{fmt(it.gross_salary)}</TableCell>
                <TableCell className="font-mono text-red-700">{fmt(it.cnas_employee)}</TableCell>
                <TableCell className="font-mono text-red-700">{fmt(it.irg)}</TableCell>
                <TableCell className="font-mono text-orange-700">{fmt(it.advance_deduction)}</TableCell>
                <TableCell className="font-mono font-bold text-green-700">{fmt(it.net_salary)}</TableCell>
                <TableCell><Badge className={STATUS_BADGE[it.status]?.cls || 'bg-gray-100'}>{STATUS_BADGE[it.status]?.label || it.status}</Badge></TableCell>
                <TableCell>
                  <Button variant="outline" size="sm" onClick={()=>openPayslip(it)} data-testid={`payslip-btn-${it.id}`}>🖨️</Button>
                  {canPay && it.status === 'approved' && (
                    <Button size="sm" className="mr-1 bg-green-600 text-white" onClick={()=>payItem(it)} data-testid={`pay-btn-${it.id}`}>💵 ادفع</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle>إنشاء فترة راتب جديدة</CardTitle></CardHeader>
        <CardContent className="flex gap-2 items-end flex-wrap">
          <div><Label>السنة</Label><Input type="number" value={newY} onChange={e=>setNewY(+e.target.value)} className="w-24" /></div>
          <div><Label>الشهر</Label><Input type="number" min="1" max="12" value={newM} onChange={e=>setNewM(+e.target.value)} className="w-20" /></div>
          <div className="flex-1"><Label>الوكالة</Label>
            <Select value={newAg} onValueChange={setNewAg}>
              <SelectTrigger data-testid="new-period-agency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">كل الوكالات</SelectItem>
                {agencies.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <Button onClick={create} data-testid="create-period-btn">➕ إنشاء</Button>
        </CardContent>
      </Card>
      <Table>
        <TableHeader><TableRow>
          <TableHead>الرقم</TableHead><TableHead>السنة/الشهر</TableHead>
          <TableHead>الوكالة</TableHead><TableHead>الموظفون</TableHead>
          <TableHead>Net</TableHead><TableHead>الحالة</TableHead><TableHead></TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {periods.map(p => {
            const st = STATUS_BADGE[p.status] || STATUS_BADGE.draft;
            const ag = agencies.find(a => a.id === p.agency_id);
            return (
              <TableRow key={p.id}>
                <TableCell className="font-mono">{p.period_no}</TableCell>
                <TableCell>{p.year}/{String(p.month).padStart(2,'0')}</TableCell>
                <TableCell>{ag?.name || (p.agency_id ? '—' : 'كل الوكالات')}</TableCell>
                <TableCell>{p.employee_count}</TableCell>
                <TableCell className="font-mono">{fmt(p.totals?.net)}</TableCell>
                <TableCell><Badge className={st.cls}>{st.label}</Badge></TableCell>
                <TableCell><Button variant="outline" size="sm" onClick={()=>openDetails(p)} data-testid={`open-period-${p.id}`}>عرض</Button></TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

// ─────────────────────────── Advances ───────────────────────────
const AdvancesTab = () => {
  const [list, setList] = useState([]);
  const [emps, setEmps] = useState([]);
  const [form, setForm] = useState({ installments: 3, start_year: new Date().getFullYear(), start_month: new Date().getMonth() + 1 });

  const load = async () => {
    const token = localStorage.getItem('token');
    const [a, e] = await Promise.all([
      axios.get(`${API}/payroll/advances`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/payroll/employees`, { headers: { Authorization: `Bearer ${token}` } }),
    ]);
    setList(a.data || []); setEmps(e.data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await axios.post(`${API}/payroll/advances`, form, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setForm({ installments: 3, start_year: new Date().getFullYear(), start_month: new Date().getMonth() + 1 });
      load();
    } catch (e) { alert('❌ ' + (e.response?.data?.detail || e.message)); }
  };

  return (
    <div className="space-y-3">
      <Card><CardHeader><CardTitle>سلفة جديدة</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-6 gap-2">
          <div className="col-span-2"><Label>الموظف</Label>
            <Select value={form.employee_id} onValueChange={v=>setForm({...form,employee_id:v})}>
              <SelectTrigger data-testid="adv-emp"><SelectValue /></SelectTrigger>
              <SelectContent>{emps.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>التاريخ</Label><Input type="date" value={form.date||''} onChange={e=>setForm({...form,date:e.target.value})} /></div>
          <div><Label>المبلغ (د.ج)</Label><Input type="number" value={form.amount||''} onChange={e=>setForm({...form,amount:+e.target.value})} /></div>
          <div><Label>عدد الأقساط</Label><Input type="number" min="1" value={form.installments} onChange={e=>setForm({...form,installments:+e.target.value})} /></div>
          <div><Label>السنة/الشهر بداية</Label>
            <div className="flex gap-1"><Input type="number" value={form.start_year} onChange={e=>setForm({...form,start_year:+e.target.value})} />
              <Input type="number" min="1" max="12" value={form.start_month} onChange={e=>setForm({...form,start_month:+e.target.value})} /></div></div>
          <div className="col-span-6"><Label>السبب</Label><Input value={form.reason||''} onChange={e=>setForm({...form,reason:e.target.value})} /></div>
          <Button className="col-span-6" onClick={save} data-testid="adv-save">💾 حفظ السلفة</Button>
        </CardContent>
      </Card>
      <Table>
        <TableHeader><TableRow>
          <TableHead>الموظف</TableHead><TableHead>التاريخ</TableHead>
          <TableHead>المبلغ</TableHead><TableHead>القسط</TableHead>
          <TableHead>المتبقي</TableHead><TableHead>الحالة</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {list.map(a => (
            <TableRow key={a.id}>
              <TableCell>{a.employee_name}</TableCell>
              <TableCell>{a.date}</TableCell>
              <TableCell className="font-mono">{fmt(a.amount)}</TableCell>
              <TableCell className="font-mono">{fmt(a.installment_amount)}</TableCell>
              <TableCell className="font-mono font-bold text-orange-700">{fmt(a.remaining_amount)}</TableCell>
              <TableCell><Badge className={a.status==='active'?'bg-blue-100':'bg-green-100'}>{a.status}</Badge></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

// ─────────────────────────── Deductions ───────────────────────────
const DeductionsTab = () => {
  const [list, setList] = useState([]);
  const [emps, setEmps] = useState([]);
  const now = new Date();
  const [form, setForm] = useState({ year: now.getFullYear(), month: now.getMonth()+1, kind: 'absence' });

  const load = async () => {
    const token = localStorage.getItem('token');
    const [a, e] = await Promise.all([
      axios.get(`${API}/payroll/deductions`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/payroll/employees`, { headers: { Authorization: `Bearer ${token}` } }),
    ]);
    setList(a.data || []); setEmps(e.data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await axios.post(`${API}/payroll/deductions`, form, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      load();
    } catch (e) { alert('❌ ' + (e.response?.data?.detail || e.message)); }
  };

  return (
    <div className="space-y-3">
      <Card><CardHeader><CardTitle>خصم جديد (غياب/تأديبي)</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-6 gap-2">
          <div className="col-span-2"><Label>الموظف</Label>
            <Select value={form.employee_id} onValueChange={v=>setForm({...form,employee_id:v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{emps.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>السنة</Label><Input type="number" value={form.year} onChange={e=>setForm({...form,year:+e.target.value})} /></div>
          <div><Label>الشهر</Label><Input type="number" min="1" max="12" value={form.month} onChange={e=>setForm({...form,month:+e.target.value})} /></div>
          <div><Label>نوع الخصم</Label>
            <Select value={form.kind} onValueChange={v=>setForm({...form,kind:v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="absence">غياب</SelectItem>
                <SelectItem value="disciplinary">تأديبي</SelectItem>
                <SelectItem value="other">آخر</SelectItem>
              </SelectContent>
            </Select></div>
          <div><Label>الأيام</Label><Input type="number" step="0.5" value={form.days||0} onChange={e=>setForm({...form,days:+e.target.value})} /></div>
          <div><Label>المبلغ (د.ج)</Label><Input type="number" value={form.amount||0} onChange={e=>setForm({...form,amount:+e.target.value})} /></div>
          <div className="col-span-4"><Label>السبب</Label><Input value={form.reason||''} onChange={e=>setForm({...form,reason:e.target.value})} /></div>
          <Button className="col-span-6" onClick={save}>💾 حفظ</Button>
        </CardContent>
      </Card>
      <Table>
        <TableHeader><TableRow>
          <TableHead>الموظف</TableHead><TableHead>السنة/الشهر</TableHead>
          <TableHead>النوع</TableHead><TableHead>الأيام</TableHead>
          <TableHead>المبلغ</TableHead><TableHead>السبب</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {list.map(d => (
            <TableRow key={d.id}>
              <TableCell>{d.employee_name}</TableCell>
              <TableCell>{d.year}/{String(d.month).padStart(2,'0')}</TableCell>
              <TableCell>{d.kind}</TableCell>
              <TableCell>{d.days}</TableCell>
              <TableCell className="font-mono">{fmt(d.amount)}</TableCell>
              <TableCell>{d.reason}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

// ─────────────────────────── Settings ───────────────────────────
const SettingsTab = () => {
  const [s, setS] = useState(null);

  const load = async () => {
    const r = await axios.get(`${API}/payroll/settings`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    setS(r.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await axios.put(`${API}/payroll/settings`, s, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      alert('✅ حُفظت الإعدادات');
    } catch (e) { alert('❌ ' + (e.response?.data?.detail || e.message)); }
  };

  if (!s) return <div>جاري التحميل...</div>;
  const acc = ['account_salary_expense','account_salary_payable','account_advances',
    'account_social_security','account_irg','account_employer_contribution',
    'account_bank','account_cash'];
  const accLabels = { account_salary_expense:'631 أعباء الرواتب', account_salary_payable:'421 مستحق للموظفين',
    account_advances:'425 السلف', account_social_security:'431 CNAS', account_irg:'442 IRG',
    account_employer_contribution:'635 أعباء رب العمل', account_bank:'512 البنك', account_cash:'53 الصندوق' };

  return (
    <div className="space-y-3">
      <Card><CardHeader><CardTitle>النسب</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-3">
          <div><Label>CNAS نسبة الموظف %</Label>
            <Input type="number" step="0.1" value={s.cnas_employee_pct} onChange={e=>setS({...s,cnas_employee_pct:+e.target.value})} data-testid="cnas-emp-pct" /></div>
          <div><Label>CNAS نسبة رب العمل %</Label>
            <Input type="number" step="0.1" value={s.cnas_employer_pct} onChange={e=>setS({...s,cnas_employer_pct:+e.target.value})} /></div>
        </CardContent></Card>

      <Card><CardHeader><CardTitle>شرائح IRG</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>من</TableHead><TableHead>إلى</TableHead>
              <TableHead>النسبة %</TableHead><TableHead>الخصم الثابت</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(s.irg_brackets||[]).map((b, i) => (
                <TableRow key={i}>
                  <TableCell><Input type="number" value={b.from||0} onChange={e=>{
                    const arr=[...s.irg_brackets]; arr[i]={...arr[i],from:+e.target.value}; setS({...s,irg_brackets:arr}); }} /></TableCell>
                  <TableCell><Input type="number" value={b.to||''} placeholder="غير محدود" onChange={e=>{
                    const arr=[...s.irg_brackets]; arr[i]={...arr[i],to:e.target.value?+e.target.value:null}; setS({...s,irg_brackets:arr}); }} /></TableCell>
                  <TableCell><Input type="number" step="0.1" value={b.rate||0} onChange={e=>{
                    const arr=[...s.irg_brackets]; arr[i]={...arr[i],rate:+e.target.value}; setS({...s,irg_brackets:arr}); }} /></TableCell>
                  <TableCell><Input type="number" value={b.deduction||0} onChange={e=>{
                    const arr=[...s.irg_brackets]; arr[i]={...arr[i],deduction:+e.target.value}; setS({...s,irg_brackets:arr}); }} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card><CardHeader><CardTitle>ربط الحسابات المحاسبية (SCF)</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-3">
          {acc.map(k => (
            <div key={k}>
              <Label>{accLabels[k]}</Label>
              <Input value={s[k]||''} onChange={e=>setS({...s,[k]:e.target.value})} data-testid={`acc-${k}`} />
            </div>
          ))}
        </CardContent>
      </Card>

      <Button onClick={save} className="w-full" data-testid="save-settings-btn">💾 حفظ الإعدادات</Button>
    </div>
  );
};

export default PayrollManagement;
